// Settings → General: the admin's switch for staff meal allowances and
// customer tabs (F-MM-N30). Both are off until an admin turns them on. The
// server accepts only {staff_allowances, customer_tabs} and refuses a
// non-admin write, so the card is admin-only as well.
import { useEffect, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import apiClient from '@/api/client'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { useToast } from '@/hooks/use-toast'
import { useAllSettings } from '@/hooks/useKitchenFlowEnabled'
import {
  HOUSE_ACCOUNTS_SETTING_KEY,
  houseAccountsSettingFrom,
  type HouseAccountsSetting,
} from '@/hooks/useHouseAccountsEnabled'
import { getStoredUserRole } from '@/lib/staff-roles'

export function HouseAccountsSettingCard() {
  const all = useAllSettings()
  const stored = all?.[HOUSE_ACCOUNTS_SETTING_KEY]
  const tabsOwing = all?.house_accounts_tabs_owing === true
  const qc = useQueryClient()
  const { toast } = useToast()
  const [value, setValue] = useState<HouseAccountsSetting>(() => houseAccountsSettingFrom(stored))
  useEffect(() => setValue(houseAccountsSettingFrom(stored)), [stored])

  const save = useMutation({
    mutationFn: async (next: HouseAccountsSetting) => {
      const res = await apiClient.updateSetting(HOUSE_ACCOUNTS_SETTING_KEY, next)
      if (!res.success) throw new Error(res.message || 'Could not save')
      return res
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['settings'] })
      toast({ title: 'Settings saved', description: 'Staff meals & tabs updated.' })
    },
    onError: (e: Error) => toast({ title: 'Save failed', description: e.message, variant: 'destructive' }),
  })

  if (getStoredUserRole() !== 'admin') return null

  const current = houseAccountsSettingFrom(stored)
  const dirty =
    value.staff_allowances !== current.staff_allowances || value.customer_tabs !== current.customer_tabs

  return (
    <Card>
      <CardHeader className="pb-4">
        <CardTitle className="text-base">Staff meals &amp; tabs</CardTitle>
        <CardDescription>
          Lets the counter charge a bill to a person&apos;s account instead of taking payment. Meal allowances are
          given in Manage Staff; tabs are opened on a guest&apos;s profile in Customers.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="flex items-start gap-3">
          <Switch
            id="house-accounts-staff"
            checked={value.staff_allowances}
            onCheckedChange={(v) => setValue((p) => ({ ...p, staff_allowances: v }))}
          />
          <div className="space-y-0.5">
            <Label htmlFor="house-accounts-staff" className="text-base">
              Staff meal allowances
            </Label>
            <p className="text-sm text-muted-foreground">
              Staff eat against a monthly allowance with a manager PIN. Meals are not counted as sales; their food
              cost shows on the monthly P&amp;L.
            </p>
          </div>
        </div>
        <div className="flex items-start gap-3">
          <Switch
            id="house-accounts-tabs"
            checked={value.customer_tabs}
            onCheckedChange={(v) => setValue((p) => ({ ...p, customer_tabs: v }))}
          />
          <div className="space-y-0.5">
            <Label htmlFor="house-accounts-tabs" className="text-base">
              Customer tabs
            </Label>
            <p className="text-sm text-muted-foreground">
              Regular guests eat now and pay later. The bill is a normal sale on the day it is eaten; payments are
              taken at the counter with &ldquo;Tab payment&rdquo;.
            </p>
            {!value.customer_tabs && tabsOwing ? (
              <p className="text-sm text-muted-foreground">
                Some tabs still owe money — Tab payment stays on the counter until they are paid off.
              </p>
            ) : null}
          </div>
        </div>
        <Button className="h-11 px-6" disabled={save.isPending || !dirty} onClick={() => save.mutate(value)}>
          Save
        </Button>
      </CardContent>
    </Card>
  )
}
